import { Button } from '~/primitives/button';
import { cn } from '~/util';
import { useFieldContext } from '~/util/hooks/useFormContext';
import {
    FieldLabelWithError,
    FieldWithErrorProps,
} from './FieldLabelWithError';
import { useDisableFormContext } from '../DisableContext';

type StringOption<V extends string> = {
    label: string;
    value: V;
};

export function SegmentedRadioField<V extends string>({
    options,
    allowClearOnReselect = true,
    className,
    buttonClassName = 'bg-background-dark text-foreground hover:bg-destructive-transparent hover:text-white',
    selectedClassName = 'bg-destructive-transparent text-white',
    ...labelProps
}: {
    options: ReadonlyArray<StringOption<V>>;
    allowClearOnReselect?: boolean;
    className?: string;
    buttonClassName?: string;
    selectedClassName?: string;
} & Omit<FieldWithErrorProps, 'hideLabelAndErrors' | 'label'>) {
    const field = useFieldContext<V | null>();
    const disabled = useDisableFormContext();

    const isSelected = (v: V) => field.state.value === v;

    const handleClick = (v: V) => {
        if (allowClearOnReselect && field.state.value === v) {
            field.handleChange(null);
        } else {
            field.handleChange(v);
        }
    };

    return (
        <FieldLabelWithError<V | null> hideLabelAndErrors {...labelProps}>
            <div
                role="radiogroup"
                aria-disabled={disabled || undefined}
                className={cn('flex flex-wrap justify-center gap-4', className)}
            >
                {options.map((opt) => {
                    const selected = isSelected(opt.value);
                    return (
                        <Button
                            key={opt.value}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            disabled={disabled}
                            onClick={() => handleClick(opt.value)}
                            className={cn(
                                'px-6 py-2 font-semibold rounded-xl transition',
                                buttonClassName,
                                selected && selectedClassName,
                                disabled && 'opacity-50 cursor-not-allowed',
                            )}
                        >
                            {opt.label}
                        </Button>
                    );
                })}
            </div>
        </FieldLabelWithError>
    );
}
